import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import { config } from '../utils/config';
import { Router } from '@angular/router';
import { LoadingService } from '../service/loading.service';
import { LoginService } from '../../app/service/auth-service/login.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _loginService?: LoginService;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private get loginService(): LoginService {
    if (!this._loginService) {
      this._loginService = this.injector.get(LoginService);
    }
    return this._loginService;
  }

  constructor(
    private router: Router,
    private loadingService: LoadingService,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let modifiedRequest = request;
    let shouldShowLoader = true;

    // Check for bypass loading header
    if (request.headers.get('x-bypass-loading') === 'true') {
      shouldShowLoader = false;
      modifiedRequest = request.clone({ headers: request.headers.delete('x-bypass-loading') });
    }

    // Add ngrok header for development
    if (config.isDevelopment) {
      modifiedRequest = modifiedRequest.clone({
        setHeaders: { 'ngrok-skip-browser-warning': 'true' }
      });
    }

    // Add authorization header if we have a valid access token
    const accessToken = this.loginService.getAccessToken();
    if (accessToken && !this.loginService.isAccessTokenExpired()) {
      modifiedRequest = this.addTokenHeader(modifiedRequest, accessToken);
    }

    // Start loading if not bypassed
    if (shouldShowLoader) {
      this.loadingService.startLoading();
    }

    // Handle normal request
    return next.handle(modifiedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          const isWhitelisted = config.auth.whitelist401Routes.some((path: string) => modifiedRequest.url.includes(path));
          if (isWhitelisted) {
            return throwError(() => error);
          }

          // If this is a login request, don't try to refresh token - just return the error
          if (modifiedRequest.url.includes('/auth/login')) {
            return throwError(() => error);
          }

          // If the failed request is the refresh endpoint, do a hard logout
          if (config.auth.refreshEndpoints.some((endpoint: string) => modifiedRequest.url.includes(endpoint))) {
            this.handleAuthFailure();
            return throwError(() => error);
          }

          return this.handle401Error(modifiedRequest, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (shouldShowLoader) {
          this.loadingService.stopLoading();
        }
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.loginService.getRefreshToken();
      if (!refreshToken) {
        this.handleAuthFailure();
        return throwError(() => new Error('No refresh token available'));
      }

      return this.loginService.refreshToken().pipe(
        switchMap((response) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(response.data.access_token);
          return next.handle(this.addTokenHeader(request, response.data.access_token));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.handleAuthFailure();
          return throwError(() => error);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private handleAuthFailure() {
    this.loginService.clearTokens();
    this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl: this.router.url }
    });
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }
}
